/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { getAssistantId } from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { ApiStatus } from '../../../../../ts';
import { Skill, SkillStatus } from '../../../../../ts/skill';
import {
  AsyncAppThunk,
  CreateAsyncThunk,
  SkillsSlice
} from '../../../../../ts/state';
import * as skillAPI from '../../../api/skills';
import { listAssistants } from '../../assistants/actions';
import { selectAllAssistants } from '../../assistants/core-selectors';
import { selectIsAccessUpdatesEnabled } from '../../features/selectors';
import { isCacheExpired } from '../../helper';
import { selectIsNewIa } from '../../instance/selectors';
import { addErrorMessage } from '../../messages/actions';
import { entityAdapter } from '../adapter';
import { selectAllSkills } from '../core-selectors';
import { checkImportProgress } from './import';

/**
 * Lists all the skills of a particular assistant.
 *
 * @param assistantId The assistant whose versionable skills we want to retrieve.
 * @param new_ia True if the assistant is New IA, false otherwise.
 */
const listAssistantSkills = async (
  assistantId: string,
  new_ia: boolean,
  useNewIAUpdatedAccess: boolean
): Promise<Skill[]> => {
  const response = await skillAPI.listSkills(
    {
      new_ia,
      assistant_id: assistantId
    },
    useNewIAUpdatedAccess,
    assistantId
  );

  return response.skills;
};

/**
 * Loads all the skills that the user may want to export from the plan limit
 * page (i.e. when a trial has finished).
 * For Classic IA, it will simply return the global skills already loaded.
 * For New IA, since it always operates within the context of an assistant,
 * it will go through all assistants and their environments and request all
 * of their skills.
 */
export const listExportableSkills = createAsyncThunk<
  Skill[],
  undefined,
  CreateAsyncThunk
>('skills/listExportableSkills', async (_, { dispatch, getState }) => {
  try {
    const new_ia = selectIsNewIa(getState());
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler new_ia on call to listSkills
    const useNewIAUpdatedAccess =
      new_ia && selectIsAccessUpdatesEnabled(getState());

    let skills: Skill[];

    if (new_ia) {
      await dispatch(listAssistants());
      const assistants = selectAllAssistants(getState());

      skills = [];

      const listSkillsPromises = assistants.map(async assistant =>
        listAssistantSkills(
          assistant.assistant_id,
          new_ia,
          useNewIAUpdatedAccess
        )
      );

      const assistantSkills = (await Promise.all(listSkillsPromises)).flat();

      skills = [...skills, ...assistantSkills];
    } else {
      skills = selectAllSkills(getState());
    }

    return skills;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const listSkills = createAsyncThunk<
  Skill[],
  string | void,
  CreateAsyncThunk
>(
  'skills/list',
  async (assistantId, { dispatch, getState }) => {
    try {
      const new_ia = selectIsNewIa(getState());
      const assistant_id = assistantId || getAssistantId(getState());
      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler new_ia on call to listSkills
      const useNewIAUpdatedAccess =
        new_ia && selectIsAccessUpdatesEnabled(getState());

      let skills: Skill[] = await listAssistantSkills(
        assistant_id,
        new_ia,
        useNewIAUpdatedAccess
      );

      //workspace_id is used throughout the code to reference the skill objects.
      //store is supposed to always return workspace_id, but there are situations where this may
      //not happen [due to schema changes] (as seen in a recent production incident). To compensate for this
      //possibility we will inject the workspace_id client side in the event that it is not supplied by the server
      skills = skills?.map(s => ({
        ...s,
        workspace_id: s.workspace_id ?? s.skill_id
      }));

      skills?.forEach(s => {
        if (s?.status === SkillStatus.PROCESSING) {
          dispatch(checkImportProgress(false, s));
        }
      });

      return skills;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const { skills } = getState();
      return skills?.listStatus !== ApiStatus.LOADING;
    }
  }
);

export const listSkillsIfRequired =
  (params: string | void): AsyncAppThunk<Promise<Skill[]>> =>
  async (dispatch, getState) => {
    const { listStatus, listFetched } = getState().skills;
    // Avoid requests if data is not stale or loa
    if (!isCacheExpired(listFetched) || listStatus === ApiStatus.LOADING) {
      return Promise.resolve(selectAllSkills(getState()));
    }
    await dispatch(listSkills(params));
    return selectAllSkills(getState());
  };

const reducer = (builder: ActionReducerMapBuilder<SkillsSlice>) => {
  builder
    .addCase(listSkills.pending, (state, _action) => {
      state.listStatus = ApiStatus.LOADING;
      state.listError = null;
    })
    .addCase(listSkills.fulfilled, (state, action) => {
      state.listStatus = ApiStatus.SUCCEEDED;
      state.listError = null;
      state.listFetched = Date.now();
      entityAdapter.setAll(state, action.payload);
    })
    .addCase(listSkills.rejected, (state, action) => {
      state.listStatus = ApiStatus.ERROR;
      state.listError = action.error;
    })
    .addCase(listExportableSkills.pending, (state, _action) => {
      state.listStatus = ApiStatus.LOADING;
      state.listError = null;
    })
    .addCase(listExportableSkills.fulfilled, (state, action) => {
      state.listStatus = ApiStatus.SUCCEEDED;
      state.listError = null;
      state.listFetched = Date.now();
      entityAdapter.setAll(state, action.payload);
    })
    .addCase(listExportableSkills.rejected, (state, action) => {
      state.listStatus = ApiStatus.ERROR;
      state.listError = action.error;
    });
};

export default reducer;
